import React, { useState } from 'react';
import { motion } from 'motion/react';
import { X, Check, FileUp } from 'lucide-react';
import { doc, setDoc } from 'firebase/firestore';
import { db, handleFirestoreError, OperationType } from '../firebase';
import { format } from 'date-fns';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  game: any;
  team: any;
  teamId: string;
  onSuccess: (gameId: string) => void;
}

export const EnterResultsModal: React.FC<Props> = ({
  isOpen, onClose, game, team, teamId, onSuccess
}) => {
  const [awayScore, setAwayScore] = useState(game?.result ? (game.isHome ? game.result.opponentScore : game.result.myScore).toString() : '');
  const [homeScore, setHomeScore] = useState(game?.result ? (game.isHome ? game.result.myScore : game.result.opponentScore).toString() : '');
  const [isSaving, setIsSaving] = useState(false);

  if (!isOpen || !game) return null;

  const handleSave = async (andUpload: boolean = false) => {
    const awayNum = parseInt(awayScore);
    const homeNum = parseInt(homeScore);

    if (isNaN(awayNum) || isNaN(homeNum)) {
      alert("Please enter valid scores for both teams.");
      return;
    }

    setIsSaving(true);
    try {
      const myScoreNum = game.isHome ? homeNum : awayNum;
      const oppScoreNum = game.isHome ? awayNum : homeNum;
      const outcome = myScoreNum > oppScoreNum ? 'W' : myScoreNum < oppScoreNum ? 'L' : 'T';

      const result = {
        myScore: myScoreNum,
        opponentScore: oppScoreNum,
        outcome
      };

      await setDoc(doc(db, `teams/${teamId}/games`, game.id), {
        status: 'completed',
        result,
        updatedAt: new Date().toISOString()
      }, { merge: true });

      if (andUpload) {
        onSuccess(game.id);
      } else {
        onClose();
      }
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, `teams/${teamId}/games`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[70] bg-surface backdrop-blur-sm flex items-center justify-center p-4"
    >
      <motion.div 
        initial={{ scale: 0.95, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        className="bg-canvas w-full max-w-md rounded-3xl p-6 border border-hairline shadow-2xl"
      >
        <div className="flex justify-between items-center mb-8">
          <div>
            <h3 className="text-xl font-black italic uppercase tracking-tight">Enter Game Result</h3>
            <p className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest mt-1">
              {format(new Date(game.date), 'MMM d, yyyy')} • {game.opponent}
            </p>
          </div>
          <button onClick={onClose} className="p-2 bg-raised rounded-full text-ink-2 hover:text-ink transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-8">
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-2 text-center">
              <label className="block text-[10px] font-semibold text-ink-2 uppercase tracking-[0.2em]">
                {game.isHome ? game.opponent : team.name}
                <span className="block text-[8px] text-ink-2 mt-0.5">(Away)</span>
              </label>
              <input 
                type="number" 
                inputMode="numeric"
                value={awayScore}
                onChange={(e) => setAwayScore(e.target.value)}
                placeholder="0"
                className="w-full bg-surface border border-hairline rounded-2xl px-4 py-6 text-ink font-black text-4xl text-center outline-none focus:border-ink focus:ring-1 focus:ring-emerald-500 transition-all"
              />
            </div>
            <div className="space-y-2 text-center">
              <label className="block text-[10px] font-semibold text-ink-2 uppercase tracking-[0.2em]">
                {game.isHome ? team.name : game.opponent}
                <span className="block text-[8px] text-ink-2 mt-0.5">(Home)</span>
              </label>
              <input 
                type="number" 
                inputMode="numeric"
                value={homeScore}
                onChange={(e) => setHomeScore(e.target.value)}
                placeholder="0"
                className="w-full bg-surface border border-hairline rounded-2xl px-4 py-6 text-ink font-black text-4xl text-center outline-none focus:border-ink focus:ring-1 focus:ring-emerald-500 transition-all"
              />
            </div>
          </div>

          <div className="space-y-3 pt-4 border-t border-hairline">
            <button 
              onClick={() => handleSave(true)}
              disabled={isSaving || !awayScore || !homeScore}
              className="w-full py-4 bg-ink text-surface font-black rounded-2xl uppercase italic flex items-center justify-center space-x-2 hover:bg-ink transition-colors shadow-[0_0_30px_rgba(16,185,129,0.2)] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FileUp className="w-5 h-5" />
              <span>{isSaving ? 'Saving...' : 'Finalize & Import Game Data'}</span>
            </button>
            <button 
              onClick={() => handleSave(false)}
              disabled={isSaving || !awayScore || !homeScore}
              className="w-full py-3 bg-raised text-ink font-black rounded-2xl uppercase italic text-sm hover:bg-hairline transition-colors disabled:opacity-50"
            >
              {isSaving ? 'Saving...' : 'Finalize Score Only'}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};
